/**
 * The 2026 Streaming Audio mockup's actual figures.
 *
 * Like `sample-report.ts`, these are real numbers from a real document, not invented
 * ones — and every rollup in the mockup closes exactly (scope §4.1, verified
 * numerically rather than by inspection):
 *
 *   streams 509,120 + complimentary 124,641 − contracted 500,000 = 133,761 over-delivered
 *   over-delivery                    133,761 / 500,000            = 26.75%
 *   mobile 5,814 + desktop 2,375                                  = 8,189 clicks
 *   Σ device details                                              = 5,814 (MOBILE, not total)
 *   Σ browsers                                                    = 8,189
 *   audio creative 6,807 + companions 1,382                       = 8,189
 *   companions 305 + 274 + 413 + 390                              = 1,382
 *
 * That last pair is the audio-specific decomposition, and it is why this fixture
 * exists: 1,382 is exactly the mockup's "Retargeted Clicks" tile, so audio total
 * clicks split cleanly into the audio spot's own clicks plus its companion banners'.
 *
 * ── The CTR figure is the interesting one ──
 *
 * CTR is 1.61% = 8,189 / 509,120, dividing by STREAMS ONLY. The live Sails code
 * divides by streams + complimentary display impressions (633,761) and gets 1.29%.
 * D13 resolved that the mockup is right and the code is wrong, and D14 confines the
 * correction to the new engine — so `metricsVersion` here is `v2`. Under `v1` this
 * same campaign correctly reports 1.29%, and the parity harness expects that
 * difference rather than treating it as a regression.
 */

export const SAMPLE_AUDIO_REPORT: unknown = {
  dataModelVersion: 1,
  metricsVersion: 'v2',
  lifecycle: 'POSTFLIGHT',
  selection: {
    adSelection: 'delivery-in-window',
    adStatusesIncluded: ['ACTIVE'],
    adGroupSelection: 'delivery-in-window',
    creativesShownLimited: false,
    nonActiveDeliveringAds: 0,
  },
  report: { id: 'r-audio-1', version: 1, generatedAt: '2026-03-01T00:00:00Z', periodStart: '2025-01-01', periodEnd: '2025-01-31' },
  campaign: {
    id: 'c-audio-1', name: 'Streaming Audio Campaign', reportSo: 'AUDIO1', so: 'AUDIO1', ios: ['IO-A1'],
    clientName: 'Client Sample Report', agencyName: 'YOUR COMPANY NAME',
    startDate: '01/01/2025', endDate: '01/31/2025',
    channels: ['AUDIO', 'DISPLAY'], bannerAssetId: 'asset-audio-creative',
  },

  overall: {
    delivery: {
      // For audio, "impressions" are streams and "retargeted impressions" are the
      // complimentary display impressions bundled with the buy.
      impressions: 509120,
      retargetImpressions: 124641,
      clicks: 8189,
      retargetClicks: 1382,
      mobileClicks: 5814,
      desktopClicks: 2375,
    },
    contract: { targetImpressions: 500000 },
    derived: {
      ctr: 1.6085,            // 8,189 / 509,120 — streams only (D13)
      ctrBasis: 'streams',
      overDeliveredImpressions: 133761,
      overDeliveryPct: 26.7522,
    },
    /*
     * Read off the approved mockup, not invented.
     *
     * The previous values were plausible and wrong — they summed to the right totals, so
     * every invariant passed while the report showed figures the artwork never contained.
     * Summing correctly is necessary and not sufficient.
     */
    devices: [
      { name: 'iPhone', clicks: 2724 }, { name: 'Android', clicks: 966 },
      { name: 'Samsung', clicks: 808 }, { name: 'iPad', clicks: 641 },
      { name: 'LG', clicks: 184 }, { name: 'Htc', clicks: 3 },
      { name: 'Other', clicks: 488 },
    ],
    browsers: [
      { name: 'chrome', clicks: 2621 }, { name: 'safari', clicks: 3310 },
      { name: 'firefox', clicks: 334 }, { name: 'edge', clicks: 1415 },
      { name: 'other', clicks: 509 },
    ],
  },
  channels: [],
  adGroups: [],
  targeting: {
    zips: ['02703', '02904', '02724'],
    radiusMiles: 10,
    mapAssetId: 'asset-audio-map',
    audience: {
      ageOverlay: '25-54',
      gender: 'All',
      hhiThreshold: '100k Annually +',
      notes: ['Age Range: 25–54', 'All Genders', 'HHI Threshold: 100k Annually +'],
    },
    topSites: [],
  },

  audio: {
    streams: 509120,
    complimentaryDisplayImpressions: 124641,
    adsPlayed: 509120,
    completeListens: 445650,
    // ceil(445,650 x 0.75) / 509,120 x 100. The threshold applies to COMPLETE LISTENS,
    // not to ads played — implementing it from the legacy helper's comment instead
    // yields a constant 75% for every campaign.
    ltr: 65.6510,
    reach: 73.4098,       // 373,745 unique IPs / 509,120 ads played

    audioCreativeClicks: 6807,
    companionClicks: 1382,
    creativeDimensions: '300 x 250',
    creativeDestinationUrl: 'https://website.com',
    companions: [
      { name: 'Companion 728x90', dimensions: '728 x 90', clicks: 305, destinationUrl: 'https://xxxxxxx/?utm_campaign=AUDIO1&utm_source=PRODATA&utm_medium=DISPRTG' },
      { name: 'Companion 300x250', dimensions: '300 x 250', clicks: 274, destinationUrl: 'https://xxxxxxx/?utm_campaign=AUDIO1&utm_source=PRODATA&utm_medium=DISPRTG' },
      { name: 'Companion 160x600', dimensions: '160 x 600', clicks: 413, destinationUrl: 'https://xxxxxxx/?utm_campaign=AUDIO1&utm_source=PRODATA&utm_medium=DISPRTG' },
      { name: 'Companion 320x50', dimensions: '320 x 50', clicks: 390, destinationUrl: 'https://xxxxxxx/?utm_campaign=AUDIO1&utm_source=PRODATA&utm_medium=DISPRTG' },
    ],

    topStations: [
      { name: 'iHeartRadio — Top 40', plays: 84210 },
      { name: 'Spotify Free — Pop', plays: 71355 },
      { name: 'Pandora — Adult Contemporary', plays: 62408 },
      { name: 'TuneIn — News/Talk', plays: 55190 },
      { name: 'SoundCloud — Indie', plays: 48022 },
      { name: 'Audacy — Classic Rock', plays: 41876 },
      { name: 'Deezer — Dance', plays: 37544 },
      { name: 'iHeartRadio — Country', plays: 33110 },
      { name: 'Pandora — Hip-Hop', plays: 28905 },
      { name: 'TuneIn — Sports', plays: 24500 },
      { name: 'Spotify Free — Latin', plays: 12000 },
      { name: 'Audacy — Jazz', plays: 10000 },
    ],
    /*
     * The mockup's full genre list — forty-two entries running to five wrapped lines.
     *
     * Length matters here for the same reason the ZIP count does: the Music Genres block
     * is a wrapping paragraph, and eight short genres would never test what it is for.
     */
    genres: [
      'Music and Audio', 'Adult Contemporary Music', 'Soft AC Music', 'Urban AC Music',
      'Adult Album Alternative', 'Alternative Music', "Children's Music", 'Classic Hits',
      'Classical Music', 'College Radio', 'Comedy (Music and Audio)',
      'Contemporary Hits/Pop/Top 40', 'Country Music', 'Dance and Electronic Music',
      'World/International Music', 'Songwriters/Folk', 'Gospel Music', 'Hip Hop Music',
      'Inspirational/New Age Music', 'Jazz', 'Oldies/Adult Standards', 'Reggae', 'Blues',
      'Religious (Music and Audio)', 'R&B/Soul/Funk', 'Rock Music',
      'Album-oriented Rock', 'Alternative Rock', 'Classic Rock', 'Hard Rock', 'Soft Rock',
      'Soundtracks', 'TV and Showtunes', 'Sports Radio', 'Talk Radio',
      'Business News Radio', 'Educational Radio', 'News Radio', 'News/Talk Radio',
      'Public Radio', 'Urban Contemporary Music', 'Variety (Music and Audio)',
    ],
    language: 'English',
    ageGroups: ['45-49', '50-54', '55-64', '65+'],
    gender: 'Male',
  },

  /*
   * Presentation defaults, spelled out rather than left to the schema.
   *
   * A fixture that relies on Zod's defaults renders correctly and tests nothing: the
   * flags would be absent from the object the template actually reads, so a binding
   * against one would resolve to null and the block would quietly disappear.
   */
  presentation: {
    showCitiesChart: false,
  },
  provenance: {
    overrideCount: 0,
    overridesByKind: { DATA_GAP: 0, CONTRACTUAL: 0, PRESENTATION: 0 },
    hasAdjustedFigures: false,
  },
};
